import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../shared/utils/pdfTurkishFont';

const EXCEL_COLORS = {
  title: 'FF16324F',
  header: 'FF16324F',
  section: 'FFE9EEF5',
  border: 'FFD3DAE5',
  text: 'FF132238'
};

const PDF_COLORS = {
  title: [22, 50, 79],
  section: [233, 238, 245],
  text: [19, 34, 56],
  border: [211, 218, 229]
};

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportNotesToExcel({ presentation, filename, companyName }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('IFRS_Notes', {
    views: [{ state: 'frozen', ySplit: 4 }]
  });

  sheet.columns = [
    { width: 14 },
    { width: 40 },
    { width: 44 },
    { width: 16 },
    { width: 16 },
    { width: 18 }
  ];

  sheet.mergeCells('A1:F1');
  sheet.getCell('A1').value = presentation.labels.statementName;
  sheet.getCell('A1').font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.title } };
  sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  sheet.mergeCells('A2:F2');
  sheet.getCell('A2').value = companyName || '';
  sheet.getCell('A2').font = { size: 10, italic: true, color: { argb: EXCEL_COLORS.text } };
  sheet.getCell('A2').alignment = { horizontal: 'center' };

  sheet.addRow([
    presentation.labels.noteCode,
    presentation.labels.noteTitle,
    presentation.labels.tables,
    presentation.labels.currentTables,
    presentation.labels.previousTables,
    'Status'
  ]);

  const headerRow = sheet.getRow(3);
  for (let columnIndex = 1; columnIndex <= 6; columnIndex += 1) {
    const cell = headerRow.getCell(columnIndex);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.header } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  }

  presentation.notes.forEach((note) => {
    const row = sheet.addRow([
      note.code,
      note.title,
      note.tables.length ? note.tables.map((table) => `${table.code} ${table.title}`).join('\n') : presentation.labels.notAvailable,
      note.currentTableCount,
      note.previousTableCount,
      note.statusLabel
    ]);
    row.eachCell((cell, index) => {
      cell.border = {
        top: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        left: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        bottom: { style: 'thin', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'thin', color: { argb: EXCEL_COLORS.border } }
      };
      cell.alignment = { vertical: 'top', horizontal: index >= 4 && index <= 5 ? 'right' : 'left', wrapText: true };
      cell.font = { color: { argb: EXCEL_COLORS.text } };
    });
  });

  const summaryRow = sheet.addRow([
    '',
    presentation.labels.totalNotes,
    '',
    presentation.stats.noteCount,
    presentation.stats.tableCount,
    ''
  ]);
  summaryRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.text } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_COLORS.section } };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

export async function exportNotesToPdf({ presentation, filename, companyName }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  doc.setFontSize(15);
  doc.setTextColor(...PDF_COLORS.title);
  doc.text(presentation.labels.statementName, 148.5, 14, { align: 'center' });

  if (companyName) {
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.text);
    doc.text(companyName, 148.5, 20, { align: 'center' });
  }

  autoTable(doc, {
    startY: companyName ? 26 : 20,
    head: [[
      presentation.labels.noteCode,
      presentation.labels.noteTitle,
      presentation.labels.tables,
      presentation.labels.currentTables,
      presentation.labels.previousTables,
      'Status'
    ]],
    body: presentation.notes.map((note) => ([
      note.code,
      note.title,
      note.tables.length ? note.tables.map((table) => `${table.code} ${table.title}`).join('\n') : presentation.labels.notAvailable,
      String(note.currentTableCount),
      String(note.previousTableCount),
      note.statusLabel
    ])),
    margin: { left: 8, right: 8 },
    theme: 'plain',
    styles: {
      font: TURKISH_FONT,
      fontSize: 7,
      textColor: PDF_COLORS.text,
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: PDF_COLORS.title,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    }
  });

  doc.save(filename);
}
